import { AsyncData, useFormData } from "#src/vue/forms/FormData.mts"
import { MessageData } from "#src/vue/forms/response.mts"
import { get } from "lodash"
import { defaultsDeep, flow, mapValues } from "lodash/fp"
import { PluginOptions, WebsocketUpdatePlugin } from "updates/js/WebsocketUpdateModule.mjs"
import { useUpdateSource } from 'updates/vue/updateSource.mts'
import type { App, Plugin } from "vue"
import { inject, provide, reactive, unref } from "vue"

const PAGE_DATA_SCRIPT_ID = "congress-page-data"

export interface UserData {
  uid: string
  name: string | null
  is_staff: boolean
  is_superuser?: true
  permissions?: string[]
}

interface RawLanguageData {
  code: string
  name: string
  icon: string
}
export interface LanguageData {
  code: string
  name: string
  icon: URL
}

export interface SiteSettings {
  favicon: string
  favicon_mime: string
  site_logo: string | null
  show_site_title: string
  site_title: string
}

interface RawPageData {
  title?: string | null
  user?: UserData
  assets?: Record<string, string>
  websocketPaths?: Record<string, string>
  site_settings: SiteSettings
  lang?: {
    locales: string[],
    data: Record<string, RawLanguageData>,
  }
  messages?: MessageData
  formData?: AsyncData | null
  dynamic?: unknown
}
export interface PageData<Extra = {}> {
  pending: boolean
  title: string | null
  user: UserData

  assets: Record<string, URL>
  websocketPaths: Record<string, string>
  urls: Record<string, string>
  site_settings: SiteSettings
  lang: {
    locales: string[],
    data: Record<string, LanguageData>,
  }
  components?: Record<string, string>

  messages: MessageData,

  formData: AsyncData | null
  dynamic: Partial<Extra>
}

const pageDataDefaults = defaultsDeep({
  pending: false,
  title: '',
  user: {
    uid: '',
    name: null,
    is_staff: false,
  },

  assets: {},
  websocketPaths: {},
  urls: {},
  lang: {locales: [], data: {}},

  messages: [],

  formData: null,
  dynamic: {},
})

export type ReplacementInput = {
  searchValue: string | RegExp
  value: string
}

const INJECTION_SYMBOL = Symbol('PageData')

function cleanPageData(data: RawPageData) {
  const result: PageData = flow(
    (data) => ({
      ...data,
      pending: false,
      // map asset strings to URLs
      "assets": mapValues(
        (path: string) => new URL(path, location.href)
      )(data.assets ?? {}),
      // map language icon assets to URLS
      'lang': {
        locales: data?.lang?.locales ?? [],
        data: mapValues(
          ({code, name, icon}) => ({
            code,
            name,
            icon: icon ? new URL(icon, location.href).toString() : icon,
          })
        )(data.lang?.data ?? {}) as Record<string, LanguageData>,
      },
      // #TODO remove in favour of new remote form logic
      "formData": (
        data.formData
          ? useFormData(data.formData)
          : null
      ) as AsyncData | null,
    }),
    pageDataDefaults,
  )(data)
  return result
}

function _getStaticPageData() {
  const scriptTag = document.getElementById(PAGE_DATA_SCRIPT_ID)
  if (scriptTag === null) {
    return {}
  }
  const inner = scriptTag?.innerHTML ?? '{}'
  scriptTag?.remove()

  return JSON.parse(inner)
}

function _parseStaticPageData() {
  const data = cleanPageData(_getStaticPageData())

  return data
}
const initialPageData = _parseStaticPageData()

/**
 * Access page data from outside vue component
 */
export function getPageData<PageDataExtension = {}>(): PageData {
  return initialPageData ?? pageDataDefaults({})
}

export function providePageData<PageDataExtension = {}>(app?: App) {
  // Re use existing injected page data if it exists
  if (app === undefined) {
    try {
      const pageData = inject(INJECTION_SYMBOL, undefined)
      if (pageData !== undefined) {
        return pageData as PageData<PageDataExtension>
      }
    } catch (error) {
      console.debug("providePageData error while trying to return existing injection", error)
    }
  }

  // generate new page data
  const pageData = reactive<PageData<PageDataExtension>>(
    initialPageData
      ? { ...initialPageData }
      : pageDataDefaults({})
  )

  console.debug("pageData", pageData)

  let abortController: AbortController | null = null

  if (app) {
    app.provide(INJECTION_SYMBOL, { pageData })
  } else {
    provide(INJECTION_SYMBOL, { pageData })
  }

  // if any page data includes components, register them globally
  pageData?.components?.forEach


  return pageData as PageData<PageDataExtension>
}

/**
 * Access page data from inside a vue component
 */
export function usePageData<PageDataExtension = {}>(): PageData<PageDataExtension> {
  const { pageData } = inject(INJECTION_SYMBOL) as { pageData: PageData<PageDataExtension> }
  return pageData
}

export default usePageData


export const PageDataPlugin: Plugin = {
  install (app: App<any>, options) {
    const { assets } = providePageData(app)
    app.use(WebsocketUpdatePlugin, assets as PluginOptions)
  }
}

/**
 * Utility function to provide the top level websocket data without necessarilly using it in the calling component.
 * grabs websocket paths from usePageData, and applies replacements from
 *
 * @param attrs a dict of key/value replacements to be applied to the websocket path. If the replacement value starts with 'pageData.' it is taken as a property path applied to the page data dict.
 */
export function provideWebsocketData<Sections = any, Static = any>(key: string, attrs?: Record<string, string | ReplacementInput> | ReplacementInput[]) {
  const pageData = usePageData()
  const { websocketPaths } = pageData

  const rawPath = unref(websocketPaths)?.[key]
  if (rawPath === undefined) {
    throw new Error(`Could not find websocket url for ${key}`)
  }

  // Create an array of ReplacementInput objects
  const _replacementInputs = (Array.isArray(attrs))
    ? attrs
    : Object.entries(attrs ?? {}).map(([key, value]) => ((typeof value === 'string') ? { searchValue: key, value, } : value));

  // create string modification functions
  const _flowInputs: ((path: string) => string)[] = _replacementInputs.map(({searchValue, value}) => {
    const _searchValue = (typeof searchValue === 'string')
      ? (!searchValue.startsWith('<') ? `<slug:${searchValue}>` : searchValue)
      : searchValue
    const _value = value.startsWith('pageData.')
      ? get({pageData: pageData}, value)
      : value
    const result =  (path: string) => path.replace(_searchValue, _value)
    return result
  })
  const path = flow(
    ..._flowInputs,
    (path) => (path.startsWith('/') ? path : '/' + path)
  )(rawPath) as string

  // The provide happens in useUpdateSource
  const { updateData, } = useUpdateSource<Sections, Static>(path)

  return { pageData, updateData, }
}
